/**
 * @file collegePersistence.js
 * @author Geoffrey Pursell
 * @desc College Persistence and Completion report plugin

*/

/**
 * @module collegePersistence
 * @desc College Persistence and Completion report plugin
 */

define('plugins/stayingInAndCompletingCollege',["underscore", "models",
    "cardboard/models", "cardboard/collections", "cardboard/util",
    "text!plugins/templates/stayingInAndCompletingCollege_persistence_table.html!strip", "text!plugins/templates/stayingInAndCompletingCollege_completion_table.html!strip", "text!plugins/templates/stayingInAndCompletingCollege_latency_table.html!strip", "text!plugins/templates/stayingInAndCompletingCollege_top25majorsEnrollment_table.html!strip", "text!plugins/templates/stayingInAndCompletingCollege_top25majorsCompletement_table.html!strip", "cardboard/pluginPieces"], 
    function (_, SLEDSModels,
        CardboardModels, CardboardCollections, Util,
        persistenceTemplate, completionTemplate, latencyTemplate, top25MajorsEnrollmentTemplate, top25MajorsCompletionTemplate, PluginPieces) {

        console.log("MODULE: stayingInAndCompletingCollege");

        var collegePersistenceVisibilityTest = function (environment) {
            return (environment.dataSources.data.postSecondaryPersistence.slices.length > 0);
        };
        var noDataMessage = "Sorry, but we don't have any of this data available for the selected criteria.";

        var exports = {

            // options for the demo cats config. since this is a multi-select
            // report, we have to use a different view object, and tell it not to
            // show grades as categories.
            options: {
                orgDetailPane: false,
                noMapSearch: true,
                forcedSummaryFilters: ["completingCollegeCOHORTID"],
                definitions: true
            },

            // each of these is the configuration that gets passed to a
            // DataBoxPaneView.
            panes: [
                {
                    title: "College Persistence",
                    explanationText: "This report shows the number and percent of high school graduates enrolled in college and continuing to their 2nd, 3rd or 4th academic year or graduating.",
                    openByDefault: true,
                    views: [
                        {
                            type: "template",
                            dataSourceName: "data",
                            template: "<h5>Percent of HS Graduates Starting College and Persisting or Graduating as of:</h5>",
                            visibleWhen: collegePersistenceVisibilityTest,
                            notVisibleMessage: noDataMessage
                        },
                        {
                            // SETUP
                            // one glob = one line series
                            // in each series, one row = one data point
                            type: "column",
                            dataSourceName: "data",
                            dataPath: "postSecondaryPersistence.slices",
                            visibleWhen: collegePersistenceVisibilityTest,

                            // "pointsAcrossRows": given that the data is
                            // two-dimensional (meaning an array of arrays, or
                            // similar structure, implying that we have more than
                            // one series to plot) each series is contained in a
                            // "column", like so:
                            //	 +---------+
                            //	 | X . . . |
                            //	 | X . . . |
                            //	 | X . . . |
                            //	 | X . . . |
                            //	 +---------+
                            dataShape: "pointsAcrossRows",
                            dataDimensions: 2,

                            // DISPLAY
                            unitString: "%",
                            showLegend: true,

                            // SERIES
                            // each series has three points, and is an organization
                            series: [
                                {

                                    seriesDisplayNamePath: "[0].organizations[0].orgName",
                                    values: [
                                        {
                                            valuePath: "[0].organizations[0].percent",
                                            pointLabelPath: "[0].label"
                                        },
                                        {
                                            valuePath: "[1].organizations[0].percent",
                                            pointLabelPath: "[1].label"
                                        },
                                        {
                                            valuePath: "[2].organizations[0].percent",
                                            pointLabelPath: "[2].label"
                                        }
                                    ]
                                },
                                {

                                    seriesDisplayNamePath: "[0].organizations[1].orgName",
                                    values: [
                                        {
                                            valuePath: "[0].organizations[1].percent",
                                            pointLabelPath: "[0].label"
                                        },
                                        {
                                            valuePath: "[1].organizations[1].percent",
                                            pointLabelPath: "[1].label"
                                        },
                                        {
                                            valuePath: "[2].organizations[1].percent",
                                            pointLabelPath: "[2].label"
                                        }
                                    ]
                                },
                                {

                                    seriesDisplayNamePath: "[0].organizations[2].orgName",
                                    values: [
                                        {
                                            valuePath: "[0].organizations[2].percent",
                                            pointLabelPath: "[0].label"
                                        },
                                        {
                                            valuePath: "[1].organizations[2].percent",
                                            pointLabelPath: "[1].label"
                                        },
                                        {
                                            valuePath: "[2].organizations[2].percent",
                                            pointLabelPath: "[2].label"
                                        }
                                    ]
                                }
                            ],
                            //errorConditions: [PluginPieces.standardErrorCondition]
                        },
                        {
                            type: "template",
                            template: persistenceTemplate,
                            visibleWhen: collegePersistenceVisibilityTest,
                            dataSourceName: "data",
                            //errorConditions: [PluginPieces.silentStandardErrorCondition]
                        }
                    ]
                },
                {
                    title: "Top 25 Majors of Enrollment",
                    explanationText: "This report shows the top 25 majors of high school graduates enrolled in college in the fall. This report shows the last reported major declared by the student at any time during their enrollment, not just during the first fall term. As time passes, the report percentages will change as more students declare their major.",
    
                    openByDefault: false,
                    views: [
                        {
                            type: "template",
                            template: top25MajorsEnrollmentTemplate,
                            dataSourceName: "data",
                               errorConditions: [
                                {
                                    valuePath: "data.data.psLastEnrollment.data[0]",
                                    is: undefined,
                                    error: {
                                        type: "NoData",
                                        message: "Sorry, but we don't have any of this data available for the selected criteria."
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    title: "Highest Undergraduate Credential Completed",
                    explanationText: "This report shows the number and percent of high school graduates completing an undergraduate certificate, diploma, or degree any time after high school graduation, by the highest credential obtained.",
                    openByDefault: false,
                    views: [
                        {
                            type: "template",
                            template: "<h5>Percent of HS Graduates Completing A Degree or Certificate at Any Time:</h5>"
                        },
                        {
                            // SETUP
                            // one glob = one line series
                            // in each series, one row = one data point
                            type: "stackedColumn",
                            dataSourceName: "data",
                            dataPath: "postSecondaryDegreeType.slices",

                            // "pointsWithinRows": given that the data is
                            // two-dimensional (meaning an array of arrays, or
                            // similar structure, implying that we have more than
                            // one series to plot) each series is contained in a
                            // "row", like so:
                            //   +---------+
                            //   | X X X X |
                            //   | . . . . |
                            //   | . . . . |
                            //   | . . . . |
                            //   +---------+
                            dataShape: "pointsWithinRows",
                            dataDimensions: 2,

                            // DISPLAY
                            // xAxisLabelPath: "subjects.label",
                            unitString: "%",
                            showLegend: true,
                            // yAxisMin: 1,
                            // yAxisMax: 50000,
                            // yAxisType: "logarithmic",

                            // SERIES
                            series: [
                                {
                                    seriesDisplayNamePath: "label",
                                    dataPath: "organizations",
                                    displayNamePath: "orgName",
                                    valuePath: "percent",

                                },
                                {
                                    seriesDisplayNamePath: "label",
                                    dataPath: "organizations",
                                    displayNamePath: "orgName",
                                    valuePath: "percent",

                                },
                                {
                                    seriesDisplayNamePath: "label",
                                    dataPath: "organizations",
                                    displayNamePath: "orgName",
                                    valuePath: "percent",

                                }
                            ],
                            //errorConditions: [PluginPieces.standardErrorCondition]
                        },

                        {
                            type: "template",
                            template: completionTemplate,
                            dataSourceName: "data",
                            //errorConditions: [PluginPieces.silentStandardErrorCondition]
                        }
                    ]
                },
                {
                    title: "Time to College Completion",
                    explanationText: "This report shows the number and percent of high school graduates completing an undergraduate credential within 4, 6, or 8 years since high school graduation.",
                    openByDefault: false,
                    views: [
                        {
                            type: "template",
                            template: "<h5>Percent of HS Graduates Completing a Degree or Certificate within:</h5>"
                        },
                        {
                            // SETUP
                            // one glob = one line series
                            // in each series, one row = one data point
                            type: "column",
                            dataSourceName: "data",
                            dataPath: "postSecondaryDegreeLatency.slices",

                            // "pointsAcrossRows": given that the data is
                            // two-dimensional (meaning an array of arrays, or
                            // similar structure, implying that we have more than
                            // one series to plot) each series is contained in a
                            // "column", like so:
                            //	 +---------+
                            //	 | X . . . |
                            //	 | X . . . |
                            //	 | X . . . |
                            //	 | X . . . |
                            //	 +---------+
                            dataShape: "pointsAcrossRows",
                            dataDimensions: 2,

                            // DISPLAY
                            unitString: "%",
                            showLegend: true,

                            // SERIES
                            // each series has three points, and is an organization
                            series: [
                                {

                                    seriesDisplayNamePath: "[0].organizations[0].orgName",
                                    values: [
                                        {
                                            valuePath: "[0].organizations[0].percent",
                                            pointLabelPath: "[0].label"
                                        },
                                        {
                                            valuePath: "[1].organizations[0].percent",
                                            pointLabelPath: "[1].label"
                                        },
                                        {
                                            valuePath: "[2].organizations[0].percent",
                                            pointLabelPath: "[2].label"
                                        }
                                    ]
                                },
                                {

                                    seriesDisplayNamePath: "[0].organizations[1].orgName",
                                    values: [
                                        {
                                            valuePath: "[0].organizations[1].percent",
                                            pointLabelPath: "[0].label"
                                        },
                                        {
                                            valuePath: "[1].organizations[1].percent",
                                            pointLabelPath: "[1].label"
                                        },
                                        {
                                            valuePath: "[2].organizations[1].percent",
                                            pointLabelPath: "[2].label"
                                        }
                                    ]
                                },
                                {

                                    seriesDisplayNamePath: "[0].organizations[2].orgName",
                                    values: [
                                        {
                                            valuePath: "[0].organizations[2].percent",
                                            pointLabelPath: "[0].label"
                                        },
                                        {
                                            valuePath: "[1].organizations[2].percent",
                                            pointLabelPath: "[1].label"
                                        },
                                        {
                                            valuePath: "[2].organizations[2].percent",
                                            pointLabelPath: "[2].label"
                                        }
                                    ]
                                }
                            ],
                            //errorConditions: [PluginPieces.standardErrorCondition]
                        },
                        {
                            type: "template",
                            template: latencyTemplate,
                            dataSourceName: "data",
                            //errorConditions: [PluginPieces.silentStandardErrorCondition]
                        }
                    ]
                },
                {
                    title: "Top 25 Majors of Completion",
                    explanationText: "This report shows the top 25 majors of high school graduates completing college for their first certificate or degree completed. ",
    
                    openByDefault: false,
                    views: [
                        {
                            type: "template",
                            template: top25MajorsCompletionTemplate,
                            dataSourceName: "data",
                               errorConditions: [
                                {
                                    valuePath: "data.data.psFinalCompletion.data[0]",
                                    is: undefined,
                                    error: {
                                        type: "NoData",
                                        message: "Sorry, but we don't have any of this data available for the selected criteria."
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    title: "More Information",
                    visibleByDefault: true,
                    openByDefault: false,
                    views: [
                        {
                            type: "template",
                            template: "<p><a href='' data-role='button' class='ui-link ui-btn ui-shadow ui-corner-all' role='button'>Download All Data</a></p><p>In these reports, Minnesota public high school graduate data includes student records with a Status End code \"8\" or \"9\". These data are not the same student cohorts used by MDE to calculate the high school graduation rate.</p> <p>For more information on postsecondary completion data, please visit the  <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=059077&RevisionSelectionMethod=latestReleased&Rendition=primary'>Data Interpretation Guide</a>.</p><p>These reports display graduates according to their last reported year of high school graduation.</p><p><a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde031876&RevisionSelectionMethod=latestReleased&Rendition=primary'>Download web report data</a> for all high schools and years. Please note this file does not include subgroup statistics. For more information, <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde031756&RevisionSelectionMethod=latestReleased&Rendition=primary'>read the help document</a>.</p>"
                        }
                    ]
                }
            ],

            filterSources: {
                orgs: {
                    modelType: SLEDSModels.SLEDSOrgList,
                    url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_filter_hsgrad_orgid.fex",
                    noViewParams: true,
                    enabled: true
                },
                filters: {
                    modelType: CardboardModels.FilterData,
                    url: "/ibi_apps/WFServlet?IBIAPP_app=SLEDSwebsvc&IBIF_ex=sledsws_getdata_filter_ecodevregion.fex",
                    enabled: true
                },
                filters2: {
                    url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_filter_completingcollegecohortid.fex",
                    enabled: true,
                    modelType: CardboardModels.FilterData
                },
                filters3: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=SLEDSwebsvc&IBIF_ex=sledsws_getdata_filter_stayingInAndCompletingCollege_dropdowns.fex"
                    },
                    enabled: true,
                }, 
                demographicCategories: {
                    modelType: CardboardCollections.DemoCatProducer,
                    url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_filter_demographics.fex",
                    enabled: true,
                    noViewParams: false,
                    isCat: true
                }
            },
            dataSources: {
                data: {
                    modelType: CardboardModels.DocumentModel,
                    url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_stayingInAndCompletingCollege.fex",
                    yearPath: "dataYear",
                    defaultParams: {
                        orgId: 999999000
                    },
                    paramMappings: {
                        HSGRAD_ORGID: "orgId",
                        orgName: null // skip this
                    },
                    postProcessor: function (data) {
                        // all we need to do is prettify all the org names!

                        var blobs = [
                            data.postSecondaryPersistence.slices,
                            data.postSecondaryDegreeType.slices,
                            data.postSecondaryDegreeLatency.slices
                        ];

                        _(blobs).each(function (slices) {
                            _(slices).each(function (slice) {
                                _(slice.organizations).each(function (org) {
                                    org.orgName = Util.Data.prettifyName(org.orgName);
                                });
                            });
                        });
                    },
                    enabled: true,
                    errorConditions: [
                        {
                            valuePath: "errorMessage",
                            isNot: null,
                            error: {
                                type: "NoData",
                                message: "Sorry, we don't have any data for your selected school/district and year."
                            }
                        }
                    ]
                }
            }
        };

        return exports;
    });
